import React, { Suspense } from 'react';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Clock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import AnswerBlock from '@/components/AnswerBlock';
import { getPostBySlug } from '@/lib/blog';
import { breakpoints } from '@/layouts/breakpoints';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

const loadMarkdownRenderer = () => import('@/components/landing/blog/MarkdownRenderer');
const MarkdownRenderer = React.lazy(loadMarkdownRenderer);
const loadArticleFooter = () => import('@/components/landing/blog/ArticleFooter');
const ArticleFooter = React.lazy(loadArticleFooter);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const formatDisplayDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

// ─── Layout ──────────────────────────────────────────────────────────────────

const Article = styled.article`
  min-height: 100vh;
  padding-top: 40px;
  background: #fff;
`;

const Container = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 56px 24px 96px;

  @media (max-width: ${breakpoints.medium}) {
    padding: 36px 16px 72px;
  }
`;

// ─── Navigation ───────────────────────────────────────────────────────────────

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 500;
  color: #999;
  text-decoration: none;
  margin-bottom: 44px;
  transition: color 0.15s;
  letter-spacing: 0.01em;

  &:hover {
    color: #0a0a0a;
  }

  svg {
    transition: transform 0.15s;
  }

  &:hover svg {
    transform: translateX(-2px);
  }
`;

// ─── Header ───────────────────────────────────────────────────────────────────

const Header = styled.header`
  margin-bottom: 40px;
`;

const DateLabel = styled.p`
  font-size: 13px;
  color: #999;
  margin: 0 0 14px;
  letter-spacing: 0.02em;
`;

const Title = styled.h1`
  font-size: 42px;
  font-weight: 750;
  letter-spacing: -0.03em;
  line-height: 1.12;
  color: #0a0a0a;
  margin: 0 0 20px;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 30px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #b0b0b0;
`;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #d0d0d0;
  flex-shrink: 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #ebebeb;
  margin: 40px 0;
`;

// ─── Hero Image ───────────────────────────────────────────────────────────────

const HeroImage = styled.div`
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 44px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.05),
    0 4px 24px rgba(0, 0, 0, 0.07);

  img {
    width: 100%;
    height: auto;
    display: block;
    aspect-ratio: 2 / 1;
    object-fit: cover;
  }
`;

// ─── States ───────────────────────────────────────────────────────────────────

const LoadingState = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 15px;
  letter-spacing: 0.01em;
`;

const NotFound = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  p {
    font-size: 18px;
    color: #888;
  }

  a {
    font-size: 14px;
    color: #0a0a0a;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function BlogArticlePage() {
  useIdlePrefetch([loadMarkdownRenderer, loadArticleFooter, loadFooter]);
  const { slug } = useParams();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    getPostBySlug(slug).then(post => {
      setPost(post);
      setLoading(false);
    });
  }, [slug]);

  if (loading) {
    return <LoadingState>Loading…</LoadingState>;
  }

  if (!post) {
    return (
      <NotFound>
        <p>Article not found</p>
        <Link to="/blog">Back to blog</Link>
      </NotFound>
    );
  }

  const absImage = post.image?.startsWith('http')
    ? post.image
    : `https://axionquant.com${post.image ?? ''}`;

  return (
    <>
      <SEOMetadata
        title={post.title}
        description={post.description}
        canonicalUrl={`https://axionquant.com/blog/${slug}`}
        ogImage={absImage}
        ogType="article"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.description,
          "author": {
            "@type": "Person",
            "name": "AxionQuant Research Team",
            "url": "https://axionquant.com/about"
          },
          "publisher": {
            "@type": "Organization",
            "name": "AxionQuant",
            "logo": {
              "@type": "ImageObject",
              "url": "https://axionquant.com/web-app-manifest-512x512.png"
            }
          },
          "datePublished": post.date,
          "dateModified": post.dateModified,
          "image": absImage,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://axionquant.com/blog/${slug}`
          }
        })}</script>
      </Helmet>

      <Article>
        <Container>
          <BackLink to="/blog">
            <ArrowLeft size={15} strokeWidth={2} />
            Back to blog
          </BackLink>

          <Header>
            <DateLabel>{formatDisplayDate(post.date)}</DateLabel>
            <Title className="title">{post.title}</Title>
            <MetaRow>
              <span>By AxionQuant Research Team</span>
              {post.readTime && (
                <>
                  <MetaDot />
                  <Clock size={13} strokeWidth={1.8} />
                  {post.readTime}
                </>
              )}
            </MetaRow>
          </Header>

          <Divider />

          <AnswerBlock answer={post.description} />

          <Suspense fallback={null}>
          <MarkdownRenderer content={post.content} />
          </Suspense>
        </Container>
      </Article>

      <Suspense fallback={null}>
      <ArticleFooter/>
      <Footer/>
      </Suspense>
    </>
  );
}
